import React, { useCallback, useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import { useSearchParams, useNavigate } from "react-router-dom";

import * as AuthApi from "api/common/Auth.api";
import * as CommunityApi from "api/koach/v2/Community.api";
import * as HomeApi from "api/koach/v2/home.api";
import { usePopup } from "contexts/PopupContext";
import StudentInfoPopupContainer from "../popup//StudentInfoPopup/StudentInfoPopup.container";
import FullPageFollowPopupContainer from "../popup/FullPageFollowPopup/FullPageFollowPopup.container";
import CongratulationInfoPopupContainer from "../popup/CongratulationInfoPopup/CongratulationInfoPopup.container";
import StudentDiaryDetailPopup from "../popup/StudentDiaryDetailPopup";
import KoachHomeComponent from "./KoachHome.component";

const POST_LIMIT = 20; // 한 번에 불러올 포스트 수

const KoachHome = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { showPopup } = usePopup();
    const { info: userInfo } = useSelector((state: any) => state.user);
    const diaryPopupOpenedRef = useRef<string | null>(null); // 이미 열린 팝업의 diaryId 추적

    // 상단 섹션 관련
    const [info, setInfo]: any = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isOnSendEmailForEmailVerification, setIsOnSendEmailForEmailVerification] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>(null);

    // 팔로워 목록 관련
    const [followerList, setFollowerList] = useState<Array<{
        id: number;
        userId: string;
        nickname?: string;
        profileImageUrl?: string;
        profileImage?: any;
    }>>([]);
    const [followerListLoading, setFollowerListLoading] = useState(true);

    // 팔로워 목록 불러오기
    const loadFollowerList = useCallback(async () => {
        try {
            setFollowerListLoading(true);
            const response = await HomeApi.fetchFollowerList(100);
            setFollowerList(response.studentList || []);
        } catch (err: any) {
            console.error('팔로워 목록 조회 실패:', err);
        } finally {
            setFollowerListLoading(false);
        }
    }, []);

    useEffect(() => {
        setIsLoading(false);
        loadFollowerList();
    }, [loadFollowerList]);

    // URL 쿼리스트링에서 diaryId 확인하여 팝업 표시
    const diaryId = searchParams.get("diaryId");
    useEffect(() => {
        // diaryId가 있고, 아직 이 diaryId로 팝업을 열지 않았을 때만 팝업 표시
        if (diaryId && diaryPopupOpenedRef.current !== diaryId) {
            diaryPopupOpenedRef.current = diaryId;
            showPopup(
                <StudentDiaryDetailPopup
                    diaryId={diaryId}
                    onClose={() => {
                        // 팝업이 닫힐 때 URL에서 쿼리스트링 제거 및 ref 초기화
                        diaryPopupOpenedRef.current = null;
                        const newSearchParams = new URLSearchParams(searchParams);
                        newSearchParams.delete("diaryId");
                        const commentId = newSearchParams.get("commentId");
                        if (commentId) {
                            newSearchParams.delete("commentId");
                        }
                        const newSearch = newSearchParams.toString();
                        navigate(`/koach${newSearch ? `?${newSearch}` : ""}`, { replace: true });
                    }}
                />
            );
        } else if (!diaryId) {
            // diaryId가 없으면 ref 초기화 (다른 페이지에서 돌아왔을 때를 대비)
            diaryPopupOpenedRef.current = null;
        }
    }, [diaryId, showPopup, navigate]);

    const handleSendEmailForEmailVerification = async () => {
        setIsOnSendEmailForEmailVerification(true);
        const result = await AuthApi.sendVerifyEmailForUser()
            .then((res: any) => {
                if (res) {
                    if (res.status === 200) {
                        const response = res.data;
                        const message = response.message;
                        alert(message);
                    }
                }
            }).catch((err: any) => {
                console.log(err);
            }).finally(() => {
                setIsOnSendEmailForEmailVerification(false);
            });
    };

    const handleShowCongratulationInfoPopup = useCallback((congratulationId: number) => {
        console.log("handleShowCongratulationInfoPopup", congratulationId);
        showPopup(<CongratulationInfoPopupContainer
            congratulationId={congratulationId} />);
    }, []);

    const handleShowStudentInfoPopup = useCallback((studentId: number) => {
        showPopup(<StudentInfoPopupContainer
            studentId={studentId} />);
    }, []);

    const handleShowFullPageFollowPopup = useCallback(() => {
        showPopup(<FullPageFollowPopupContainer />);
    }, []);

    return (
        <KoachHomeComponent
            info={info}
            isLoading={isLoading}
            isOnSendEmailForEmailVerification={isOnSendEmailForEmailVerification}
            handleSendEmailForEmailVerification={handleSendEmailForEmailVerification}
            handleShowCongratulationInfoPopup={handleShowCongratulationInfoPopup}
            handleShowStudentInfoPopup={handleShowStudentInfoPopup}
            handleShowFullPageFollowPopup={handleShowFullPageFollowPopup}
            errorMessage={errorMessage}
            followerList={followerList}
            followerListLoading={followerListLoading}
        />
    );
};

export default KoachHome;